import React, { useState } from "react";
import { ShieldCheck, X } from "lucide-react";
import { api } from "@/lib/api";

function authHeaders() {
  const t = localStorage.getItem("fliboe_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
}

/**
 * One-time content policy click-through. Enforced by the /author/* endpoints
 * (they return 428 until this is accepted). Same acceptance also unlocks Pulse
 * post generation for the same user.
 */
export default function ContentPolicyModal({ open, onAccepted }) {
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  if (!open) return null;

  const accept = async () => {
    if (!confirmed) { setErr("Please confirm you agree to all points."); return; }
    setSubmitting(true); setErr("");
    try {
      const { data } = await api.post("/author/accept-policy", { version: "2026-02-flip-and-pulse" }, { headers: authHeaders() });
      onAccepted?.(data);
    } catch (e) { setErr(e?.response?.data?.detail || "Could not save acceptance."); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="policy-modal">
      <div className="fl-card max-w-lg w-full p-6 md:p-8 bg-gradient-to-br from-violet-500/10 via-black/80 to-fuchsia-500/10 border-violet-400/30">
        <div className="flex items-center gap-3">
          <ShieldCheck className="w-6 h-6 text-violet-300" />
          <h2 className="fl-display text-2xl font-bold text-white">Content policy</h2>
        </div>
        <p className="text-slate-300 text-sm mt-3 leading-relaxed">
          Fliboe Flip publishes what you upload — no manual review. Before you start, please confirm the ground rules:
        </p>
        <ul className="text-slate-300 text-sm mt-4 space-y-2 leading-relaxed">
          <li><span className="text-violet-300 mr-1.5">•</span> I own the rights to every book and chapter I upload, or have permission to publish it.</li>
          <li><span className="text-violet-300 mr-1.5">•</span> No hate speech, harassment, sexual content involving minors, or content that incites violence.</li>
          <li><span className="text-violet-300 mr-1.5">•</span> No infringement — quoting another author&apos;s work as your own will be flagged and removed.</li>
          <li><span className="text-violet-300 mr-1.5">•</span> Fliboe may take down or block any content that violates these rules or applicable law. Repeat violations end the account.</li>
        </ul>

        <label className="flex items-start gap-3 mt-6 text-sm text-white/85 cursor-pointer" data-testid="policy-checkbox-label">
          <input type="checkbox" checked={confirmed} onChange={e => setConfirmed(e.target.checked)} className="mt-1" data-testid="policy-checkbox" />
          <span>I agree to the content policy and confirm I have the rights to everything I upload.</span>
        </label>

        {err && <div className="text-red-300 text-sm mt-3" data-testid="policy-err">{err}</div>}

        <div className="mt-6 flex flex-wrap gap-3 justify-end">
          <a href="/" className="fl-btn ghost !py-2 !px-4 text-sm inline-flex items-center gap-1" data-testid="policy-cancel"><X className="w-3.5 h-3.5" /> Cancel</a>
          <button onClick={accept} disabled={submitting} className="fl-btn !py-2 !px-5 text-sm" data-testid="policy-accept">
            {submitting ? "Saving…" : "Agree & continue"}
          </button>
        </div>
      </div>
    </div>
  );
}
